import type { ReactElement } from 'react';
import { InlineEditableText } from '@reflex/ui/components/forms/InlineEditableText';
import { PanelTitle, panelTitleTypography } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import { toast } from 'sonner';
import { useUpdateAgent } from '@/hooks/agents/useAgents';

interface EditableAgentNameProps {
  agentId: string;
  /** Current name; also the display/edit seed value. */
  name: string;
  /** Suppress the edit affordance and render the name read-only. */
  disabled?: boolean;
  /** Element for the rendered title. Mirrors `PanelTitle`'s `as`. */
  as?: 'h1' | 'h2' | 'h3' | 'span' | 'p';
  /** Size step for the rendered title. Mirrors `PanelTitle`'s `size`. */
  size?: 'sm' | 'default';
  /** Pass false to wrap instead of truncating. Mirrors `PanelTitle`. */
  truncate?: boolean;
  /** Docs affordance beside the title. Mirrors `PanelTitle`. */
  docs?: string | ReactElement;
  /**
   * Layout and emphasis overrides (`min-w-0`, `font-semibold`, ...). Applied to
   * the title and the edit shell from this one value, so the read-only and
   * editing states cannot render different typography. Keep it to typography
   * and `min-w-0`: see the `className` note on `InlineEditableText`.
   */
  className?: string;
}

/**
 * Click-to-rename agent title. Owns the update mutation and failure toast so
 * every surface that shows an editable name (stream header, sidebar rows,
 * detail panel) stays in sync and reports errors the same way.
 *
 * It also renders the `PanelTitle` itself rather than taking it as a child.
 * That is deliberate: the editing `<input>` inherits its font from the shell,
 * so the shell has to be styled to match the title. Deriving both from the one
 * `size`/`className` pair here removes the only thing call sites used to get
 * wrong — a shell whose typography had drifted from the title it replaces.
 */
export function EditableAgentName({
  agentId,
  name,
  disabled,
  as = 'h1',
  size = 'default',
  truncate = true,
  docs,
  className,
}: EditableAgentNameProps) {
  const updateAgent = useUpdateAgent();
  return (
    <InlineEditableText
      value={name}
      ariaLabel="Agent name"
      disabled={disabled}
      className={cn(panelTitleTypography(size), className)}
      onCommit={(next) =>
        updateAgent.mutate(
          { id: agentId, name: next },
          {
            onError: (err) =>
              toast.error('Failed to rename agent', {
                description: err instanceof Error ? err.message : undefined,
              }),
          },
        )
      }
    >
      <PanelTitle as={as} size={size} truncate={truncate} docs={docs} className={className}>
        {name}
      </PanelTitle>
    </InlineEditableText>
  );
}
